import { handleApiError, NetworkError } from "../utils/Errors";
const URLS = {
    SEND_MOBILE_VERIFICATION: "/code/send",
    CONFIRM_MOBILE_VERIFICATION: "/code/confirm",
    LOGIN: "/login/pin",
    SIGNUP: "/v4/signup",
    GENERATE_CURP: "/curp/generate",
    VALIDATE_CURP: "/v3/curp/validate",
    AUTOCOMPLETE_ADDRESS: "/address/autocomplete",
    ADDRESS: "/address",
};
class NeloApiClient {
    constructor(headers, baseUrl, sendAnalyticsEvent, temporaryUserUuid, advertisingId, accessToken) {
        this.headersInstance = headers;
        this.baseUrl = baseUrl;
        this.sendAnalyticsEvent = sendAnalyticsEvent ?? (() => { });
        this.temporaryUserUuid = temporaryUserUuid;
        this.advertisingId = advertisingId;
        this.accessToken = accessToken;
    }
    baseUrl;
    temporaryUserUuid;
    advertisingId;
    accessToken;
    headersInstance;
    sendAnalyticsEvent;
    /**
     * The session ID used for tracking user sessions
     */
    sessionId = "";
    /**
     * Updates the access token for authenticated requests
     * @param token The new access token
     */
    setAccessToken(token) {
        this.accessToken = token;
    }
    emitAnalyticEvent(eventName, status, message) {
        this.sendAnalyticsEvent(eventName, { status, message });
    }
    getAuthorizationHeader() {
        return this.accessToken ? `Bearer ${this.accessToken}` : null;
    }
    /**
     * Sets the session ID for tracking user sessions
     * @param sessionId The session ID
     */
    setSessionId(sessionId) {
        this.sessionId = sessionId;
    }
    /**
     * Gets the headers for the request
     * @param isJsonContent Whether the request has JSON content
     * @returns Headers for the request
     */
    async getHeaders(isJsonContent) {
        const headers = await this.headersInstance.getHeaders();
        const authHeader = this.getAuthorizationHeader();
        if (authHeader) {
            headers.set("Authorization", authHeader);
        }
        if (isJsonContent) {
            headers.set("Content-Type", "application/json; charset=utf-8");
        }
        if (this.temporaryUserUuid) {
            headers.set("user-uuid", this.temporaryUserUuid);
        }
        if (this.advertisingId) {
            headers.set("x-device-advert-id", this.advertisingId);
        }
        if (this.sessionId) {
            headers.set("session-id", this.sessionId);
        }
        // Add pseudo-id header (same as user-uuid)
        if (this.temporaryUserUuid) {
            headers.set("x-user-pseudo-id", this.temporaryUserUuid);
        }
        return headers;
    }
    async makeRequestInternal(method, url, body, event) {
        const headers = await this.getHeaders(typeof body !== "undefined");
        const options = {
            method,
            mode: "cors",
            headers: headers,
        };
        options.body = body;
        const fullUrl = `${this.baseUrl}${url}`;
        try {
            const response = await fetch(fullUrl, options);
            event && this.emitAnalyticEvent(event, response.status);
            return response;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            this.sendAnalyticsEvent("signup_lib_fetch_error", {
                fullUrl,
                error: errorMessage,
            });
            // Transform to a NetworkError with a helpful message
            throw new NetworkError(`Network request failed: ${errorMessage}`);
        }
    }
    async post(url, data, event) {
        return this.makeRequestInternal("POST", url, JSON.stringify(data), event);
    }
    async put(url, data, event) {
        return this.makeRequestInternal("PUT", url, JSON.stringify(data), event);
    }
    async get(baseUrl, data, event) {
        const url = this.getUrlWithQueryString(baseUrl, data);
        return this.makeRequestInternal("GET", url, undefined, event);
    }
    getUrlWithQueryString(baseUrl, data) {
        if (!data) {
            return baseUrl;
        }
        const components = Object.entries(data).map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        return `${baseUrl}?${components.join("&")}`;
    }
    createMobileVerification(requestBody) {
        return this.post(URLS.SEND_MOBILE_VERIFICATION, requestBody, "signup_lib_send_verification_code_request");
    }
    confirmMobileVerification(requestBody) {
        return this.put(URLS.CONFIRM_MOBILE_VERIFICATION, requestBody, "signup_lib_confirm_verification_code_request");
    }
    /**
     * Submits signup information to create a new user account
     *
     * @param requestBody - The signup request data
     * @returns A Promise with the API response
     */
    signup(requestBody) {
        return this.post(URLS.SIGNUP, requestBody, "signup_lib_signup_request");
    }
    /**
     * Submits signup information and returns the parsed response
     *
     * @param requestBody - The signup request data
     * @returns A Promise with the parsed signup response
     * @throws {UserAlreadyExistsError} If the user already exists
     * @throws {ApiError} If the API returns an error response
     * @throws {NetworkError} If the network request fails
     */
    async signupWithData(requestBody) {
        const response = await this.signup(requestBody);
        if (!response.ok) {
            const error = await handleApiError(response);
            throw error;
        }
        return response.json();
    }
    /**
     * Validates a CURP (Clave Única de Registro de Población) with the Nelo API
     *
     * @param curp - The CURP string to validate
     * @returns A Promise with the validation response
     */
    validateCurp(curp) {
        return this.get(URLS.VALIDATE_CURP, { curp }, "signup_lib_validate_curp_request");
    }
    /**
     * Validates a CURP and parses the response
     *
     * @param curp - The CURP string to validate
     * @returns A Promise with the parsed validation response
     * @throws {ApiError} If the API returns an error response
     * @throws {NetworkError} If the network request fails
     */
    async validateCurpWithData(curp) {
        const response = await this.validateCurp(curp);
        if (!response.ok) {
            const error = await handleApiError(response);
            throw error;
        }
        return response.json();
    }
    /**
     * Generates a CURP based on personal information
     *
     * @param request - The CURP details needed to generate a CURP
     * @param phoneNumber - The user's phone number to associate with the CURP
     * @returns A Promise with the API response
     */
    generateCurp(request, phoneNumber) {
        const requestBody = {
            name: request.firstName,
            paternalLastName: request.paternalLastName,
            maternalLastName: request.maternalLastName,
            gender: request.gender,
            dayOfBirth: request.dayOfBirth,
            monthOfBirth: request.monthOfBirth,
            yearOfBirth: request.yearOfBirth,
            stateOfBirth: request.stateOfBirth,
            phoneNumber: phoneNumber,
        };
        return this.post(URLS.GENERATE_CURP, requestBody, "signup_lib_generate_curp_request");
    }
    /**
     * Generates a CURP and returns the parsed response
     *
     * @param request - The CURP details needed to generate a CURP
     * @param phoneNumber - The user's phone number to associate with the CURP
     * @returns A Promise with the parsed CURP generation response
     * @throws {ApiError} If the API returns an error response
     * @throws {NetworkError} If the network request fails
     */
    async generateCurpWithData(request, phoneNumber) {
        const response = await this.generateCurp(request, phoneNumber);
        if (!response.ok) {
            const error = await handleApiError(response);
            throw error;
        }
        return response.json();
    }
    /**
     * Searches for address suggestions based on user input
     *
     * @param input - The partial address string entered by the user
     * @param sessionId - Optional session ID for the Places API (should be a UUID)
     * @returns A Promise with the API response
     */
    autocompleteAddress(input, sessionId) {
        // Use provided session ID or fall back to the client session ID
        const session = sessionId || this.sessionId;
        return this.get(URLS.AUTOCOMPLETE_ADDRESS, { input, session }, "signup_lib_address_autocomplete_request");
    }
    /**
     * Searches for address suggestions and parses the response
     *
     * @param input - The partial address string entered by the user
     * @param sessionId - Optional session ID for the Places API (should be a UUID)
     * @returns A Promise with the parsed address autocomplete response
     * @throws {ApiError} If the API returns an error response
     * @throws {NetworkError} If the network request fails
     */
    async autocompleteAddressWithData(input, sessionId) {
        const response = await this.autocompleteAddress(input, sessionId);
        if (!response.ok) {
            const error = await handleApiError(response);
            throw error;
        }
        return response.json();
    }
    /**
     * Gets detailed address information based on a place ID
     *
     * @param placeId - The ID of the place to get details for
     * @param sessionId - Optional session ID for the Places API (should be a UUID)
     * @returns A Promise with the API response
     */
    getAddress(placeId, sessionId) {
        // Use provided session ID or fall back to the client session ID
        const session = sessionId || this.sessionId;
        return this.get(URLS.ADDRESS, { placeId, session }, "signup_lib_address_request");
    }
    /**
     * Gets detailed address information and parses the response
     *
     * @param placeId - The ID of the place to get details for
     * @param sessionId - Optional session ID for the Places API (should be a UUID)
     * @returns A Promise with the parsed address details response
     * @throws {ApiError} If the API returns an error response
     * @throws {NetworkError} If the network request fails
     */
    async getAddressWithData(placeId, sessionId) {
        const response = await this.getAddress(placeId, sessionId);
        if (!response.ok) {
            const error = await handleApiError(response);
            throw error;
        }
        return response.json();
    }
}
export default NeloApiClient;
